import { useCallback, useMemo, useState } from "react";
import type { FranceToParisMappedFilters } from "../../../../api/pipeline.service.js";
import { ContextSelectors } from "./ContextSelectors.js";
import { MappingBuilder } from "./MappingBuilder.js";
import { RunPanel } from "./RunPanel.js";
import {
  usePracticeOptions,
  usePublicationOptions,
  useRegionOptions,
} from "./useTaxonomyOptions.js";
import type { CollectedMapping, PracticeOption, PublicationOption, RegionOption } from "./types.js";
import styles from "../PipelineBrowser.module.css";

const DEFAULT_LANGUAGE = "fr";

function currentYear(): string {
  return String(new Date().getFullYear());
}

function findName(options: { id: number; name: string }[], id: number | null): string {
  if (id == null) return "";
  return options.find(o => o.id === id)?.name ?? "";
}

// New "France → Paris" tab: copy FR testimonials using user-chosen practice
// mappings. Distinct from the hardcoded FranceToParisSection in the Fixes tab.
export function FranceToParisMappedTab() {
  const [edition, setEdition] = useState(currentYear());
  const [sourcePubId, setSourcePubId] = useState<number | null>(null);
  const [sourceRegionId, setSourceRegionId] = useState<number | null>(null);
  const [destPubId, setDestPubId] = useState<number | null>(null);
  const [destRegionId, setDestRegionId] = useState<number | null>(null);
  const [mappings, setMappings] = useState<CollectedMapping[]>([]);

  const publications = usePublicationOptions(edition);
  const sourceRegions = useRegionOptions(sourcePubId);
  const destRegions = useRegionOptions(destPubId);
  const sourcePractices = usePracticeOptions(sourcePubId, sourceRegionId);
  const destPractices = usePracticeOptions(destPubId, destRegionId);

  // Reset dependent selections when a parent selection changes.
  const handleSourcePub = useCallback((id: number | null) => {
    setSourcePubId(id);
    setSourceRegionId(null);
  }, []);
  const handleDestPub = useCallback((id: number | null) => {
    setDestPubId(id);
    setDestRegionId(null);
  }, []);

  const handleAdd = useCallback((source: PracticeOption, dest: PracticeOption) => {
    const key = `${source.editorialCategoryId}:${dest.editorialCategoryId}`;
    setMappings(prev => (prev.some(m => m.key === key) ? prev : [...prev, { key, source, dest }]));
  }, []);
  const handleRemove = useCallback((key: string) => {
    setMappings(prev => prev.filter(m => m.key !== key));
  }, []);

  const contextReady =
    sourcePubId != null && sourceRegionId != null && destPubId != null && destRegionId != null;

  const filters: FranceToParisMappedFilters | null = useMemo(() => {
    if (!contextReady || mappings.length === 0 || destPubId == null) return null;
    return {
      sourcePublicationId: sourcePubId,
      targetPublicationId: destPubId,
      targetPublicationName: findName(publications.options, destPubId),
      targetRegionName: findName(destRegions.options, destRegionId),
      language: DEFAULT_LANGUAGE,
      mappings: mappings.map(m => ({
        sourceEditorialCategoryId: m.source.editorialCategoryId,
        targetEditorialCategoryId: m.dest.editorialCategoryId,
        targetPracticeAreaId: m.dest.practiceAreaId,
        targetPracticeAreaName: m.dest.practiceAreaName,
        targetTableId: m.dest.tableId,
        targetTableName: m.dest.tableName,
      })),
    };
  }, [
    contextReady,
    mappings,
    sourcePubId,
    destPubId,
    destRegionId,
    publications.options,
    destRegions.options,
  ]);

  return (
    <div>
      <div className={styles.infoBanner}>
        This tool copies all testimonials from the EMEA publication&apos;s France region to the
        Paris publication. Publication IDs change every year, so select the correct Source and
        Destination publication/region in the fields below before mapping. Before mapping, confirm
        all testimonials from the France region have already been translated to French — if not, go
        to the &quot;Fixes&quot; tab and run the Translate (FR) step first.
      </div>

      <ContextSelectors
        edition={edition}
        onEditionChange={setEdition}
        source={buildSide(
          publications,
          sourceRegions,
          sourcePubId,
          sourceRegionId,
          handleSourcePub,
          setSourceRegionId,
        )}
        dest={buildSide(
          publications,
          destRegions,
          destPubId,
          destRegionId,
          handleDestPub,
          setDestRegionId,
        )}
      />

      <MappingBuilder
        source={sourcePractices}
        dest={destPractices}
        mappings={mappings}
        onAdd={handleAdd}
        onRemove={handleRemove}
      />

      {filters ? (
        <RunPanel filters={filters} />
      ) : (
        <div className={styles.syncStateMessage}>
          Select the publication and region (source + destination) and add at least one mapping to
          run the copy.
        </div>
      )}
    </div>
  );
}

type SideConfig = {
  publications: { options: PublicationOption[]; loading: boolean; error: string | null };
  regions: { options: RegionOption[]; loading: boolean; error: string | null };
  publicationId: number | null;
  regionId: number | null;
  onPublicationChange: (id: number | null) => void;
  onRegionChange: (id: number | null) => void;
};

function buildSide(
  publications: { options: PublicationOption[]; loading: boolean; error: string | null },
  regions: { options: RegionOption[]; loading: boolean; error: string | null },
  publicationId: number | null,
  regionId: number | null,
  onPublicationChange: (id: number | null) => void,
  onRegionChange: (id: number | null) => void,
): SideConfig {
  return {
    publications,
    regions,
    publicationId,
    regionId,
    onPublicationChange,
    onRegionChange,
  };
}

export default FranceToParisMappedTab;
